// frontend/src/components/cspe/DocumentActionButtons.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import LiasseValidationModal from './LiasseValidationModal';
import { RotateCw } from 'lucide-react';
import {
  FiEye,
  FiDownload,
  FiCpu,
  FiLoader,
  FiCheckSquare,
  FiXSquare,
  FiEdit3
} from 'react-icons/fi';

/**
 * DocumentActionButtons (Version simplifiée pour Liasse Fiscale)
 *
 * Gère les actions pour un document de type "liasse fiscale".
 *
 * Props :
 *   - doc              : L'objet du document.
 *   - onValidate(docId): Callback pour valider le document.
 *   - onReject(docId)  : Callback pour rejeter le document.
 *   - onProcessLiasse(doc): Callback pour lancer le traitement OCR de la liasse.
 *   - isProcessingLiasse (bool): Indique si un traitement est déjà en cours.
 */
const DocumentActionButtons = ({
  doc,
  onValidate,
  onReject,
  onProcessLiasse,
  isProcessingLiasse,
}) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [showLiasseValidation, setShowLiasseValidation] = useState(false);

  // --- Charger l'URL signée pour Aperçu / Téléchargement ---
  useEffect(() => {
    const generateSignedUrl = async () => {
      try {
        // La logique est la même, mais on la garde car elle est nécessaire
        if (doc.s3_bucket && doc.s3_key) {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) return;

          const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
          const apiUrl = `${backendUrl}/api/cspe/documents/uploads-s3/${doc.id}/download`;
          
          const response = await fetch(apiUrl, {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          });

          if (response.ok) {
            const result = await response.json();
            setSignedUrl(result.url || null);
          } else {
            console.error(`Erreur API (${response.status}) pour doc ${doc.id}`);
          }
        }
      } catch (e) {
        console.error(`Exception génération URL signée pour doc ${doc.id}:`, e);
      }
    };
    generateSignedUrl();
  }, [doc.id, doc.s3_bucket, doc.s3_key]);

  const handleLiasseValidationComplete = (docId, action) => {
  // La modal se ferme elle-même :
  //   • après validation → via le bouton "Fermer" du bandeau vert
  //   • après rejet     → via handleClose() interne qui appelle onClose()
  // On rafraîchit juste les données parentes
  if (typeof onValidate === 'function') {
    onValidate(docId);
  }
};

  return (
    <>
      {/* --- Boutons communs : Aperçu et Télécharger --- */}
      {signedUrl && (
        <button
          onClick={() => window.open(signedUrl, "_blank")}
          className="p-1.5 text-indigo-600 hover:text-indigo-800"
          title="Aperçu"
        >
          <FiEye className="h-5 w-5" />
        </button>
      )}

      {/* --- Actions spécifiques au statut de la liasse --- */}

      {/* 1. Si le document est en attente, on peut Valider (seulement si OCR fait) / Rejeter */}
      {(doc.status === "En attente" || doc.status === "En attente de validation") && (
        <>
          {/* 🟣 MODIFICATION : On n'affiche le bouton valider que si l'OCR est fait */}
          {doc.ocr_done && (
            <button
              onClick={() => onValidate(doc.id)}
              className="ml-2 p-1.5 text-green-600 hover:text-green-800"
              title="Valider"
            >
              <FiCheckSquare className="h-5 w-5" />
            </button>
          )}
          
          <button
            onClick={() => {
                const reason = prompt("Motif du rejet :");
                if (reason) onReject(doc.id, reason);
            }}
            className="ml-1 p-1.5 text-red-600 hover:text-red-800"
            title="Rejeter"
          >
            <FiXSquare className="h-5 w-5" />
          </button>
        </>
      )}

      {/* 2. Si le document est validé mais pas encore traité par l'OCR */}
      {doc.status === "Validé" && !doc.ocr_done && (
        <button
          onClick={() => onProcessLiasse(doc)}
          disabled={isProcessingLiasse}
          className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400"
          title="Lancer l'analyse OCR"
        >
          {isProcessingLiasse ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiCpu className="h-4 w-4" />}
          {/* 🟣 MODIFICATION ICI */}
          <span className="ml-1.5">Lancer OCR</span> 
        </button>
      )}
      
      {/* 3. Si l'OCR a été fait */}
      {doc.ocr_done && ![
        'Validé', 
        'Validé manuellement', 
        'Rejeté', 
        'Rejeté par admin', 
        'En attente de validation manuelle'
      ].includes(doc.status) && (
        <FiCheckSquare
          className="ml-2 h-5 w-5 text-green-600"
          title="Liasse fiscale traitée"
        />
      )}

      {/* 4. Si une validation manuelle est requise ou erreur */}
      {(doc.status === 'En attente de validation' || doc.status === 'Erreur critique' || doc.status === 'Erreur extraction') && (
        <button
          onClick={() => onProcessLiasse(doc)}
          disabled={isProcessingLiasse}
          className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
          title={doc.ocr_done ? "Relancer l'analyse OCR" : "Lancer l'analyse OCR"}
        >
          {isProcessingLiasse ? (
            <FiLoader className="h-4 w-4 animate-spin" />
          ) : (
            <RotateCw className="h-4 w-4" />
          )}
          {/* 🟣 MODIFICATION ICI */}
          <span className="ml-1.5">{doc.ocr_done ? 'Relancer OCR' : 'Lancer OCR'}</span> 
        </button>
      )}

      {/* 5. Voir données extraites (et valider depuis la modale) */}
      {(doc.status === 'En attente de validation manuelle' ||
          doc.status === 'Validé manuellement' ||
          doc.status === 'Validé') && (
          <button
            onClick={() => setShowLiasseValidation(true)}
            className={`ml-2 inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md ${
              doc.status === 'En attente de validation manuelle'
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
            title="Voir les données extraites de la liasse"
          >
            <FiEdit3 className="h-4 w-4" />
            <span className="ml-1.5">Voir données</span>
          </button>
        )}

      {/* La modale reste nécessaire pour la validation manuelle */}
      <LiasseValidationModal
        isOpen={showLiasseValidation}
        onClose={() => setShowLiasseValidation(false)}
        documentId={doc.id}
        documentName={doc.original_file_name}
        onValidationComplete={handleLiasseValidationComplete}
      />
    </>
  );
};

export default DocumentActionButtons;